import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import Layout from '../components/Layout';
import { FileText, Printer, ArrowRight, Trash2, Mail, Send, CreditCard, Link2, Unlink } from 'lucide-react';
import AttachDevisModal from '../components/AttachDevisModal';

const DevisFacturesPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailModal, setEmailModal] = useState({ show: false, docId: '', email: '', ref: '', type: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [attachModal, setAttachModal] = useState({ show: false, devisId: '', devisRef: '', devisTotal: 0 });
  const [disableOnlinePayment, setDisableOnlinePayment] = useState(false);

  // Valeurs backend FR (doc_type / status) -> libellés i18n pour l'AFFICHAGE uniquement.
  // La logique (ex. doc.status === 'devis') reste inchangée ; repli sur la valeur brute.
  const DOC_LABEL_KEYS = {
    devis: 'devisFactures.labelDevis',
    facture: 'devisFactures.labelFacture',
    commande: 'devisFactures.labelCommande',
    avoir: 'devisFactures.labelAvoir',
    paye: 'devisFactures.labelPaye',
    'payé': 'devisFactures.labelPaye',
    annule: 'devisFactures.labelAnnule',
    'annulé': 'devisFactures.labelAnnule',
    en_attente: 'devisFactures.labelEnAttente',
    'en attente': 'devisFactures.labelEnAttente',
  };
  const mapLabel = (v) => {
    if (v == null || v === '') return v;
    const k = DOC_LABEL_KEYS[String(v).toLowerCase()];
    return k ? t(k) : v;
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const [docsRes, settingsRes] = await Promise.all([
        api.get('/calcul/documents'),
        api.get('/company-settings').catch(() => ({ data: {} })),
      ]);
      setDocuments(docsRes.data);
      setDisableOnlinePayment(settingsRes.data?.document_settings?.disable_online_payment || false);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const openBlobForPrint = (blob) => {
    const url = window.URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank', 'width=900,height=700');
    if (!printWindow) {
      alert(t('devisFactures.printPopupBlocked'));
      return;
    }
    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
    };
    printWindow.addEventListener('load', triggerPrint);
    setTimeout(triggerPrint, 1200);
    setTimeout(() => window.URL.revokeObjectURL(url), 8000);
  };

  const handlePrintTicket = async (docId) => {
    try {
      await printTicket80mm(api, { type: 'calcul_doc', ref: docId });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('devisFactures.errorPrintTicket'));
    }
  };

  const handlePrintA4 = async (docId) => {
    try {
      const response = await api.get(`/calcul/documents/${docId}/a4`, { responseType: 'blob' });
      openBlobForPrint(response.data);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('devisFactures.errorPrintA4'));
    }
  };

  const handleConvertCommande = async (docId) => {
    if (!window.confirm(t('devis.convertConfirm'))) return;
    try {
      await api.post(`/calcul/documents/${docId}/convert/commande`);
      fetchDocuments();
      alert(t('devis.orderCreated'));
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('devisFactures.errorConvert'));
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(t('devis.deleteConfirm'))) return;
    try {
      await api.delete(`/calcul/documents/${docId}`);
      fetchDocuments();
      alert(t('devis.deleted'));
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('devisFactures.errorDelete'));
    }
  };

  const handleSendEmail = async () => {
    if (!emailModal.email || !emailModal.email.includes('@')) {
      alert(t('devisFactures.errorInvalidEmail'));
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/calcul/documents/${emailModal.docId}/send-email`, {
        email: emailModal.email
      });
      alert(t('devisFactures.emailSent', { type: mapLabel(emailModal.type), email: emailModal.email }));
      setEmailModal({ show: false, docId: '', email: '', ref: '', type: '' });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('devisFactures.errorSendEmail'));
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailModal = (doc) => {
    setEmailModal({
      show: true,
      docId: doc.id,
      email: doc.client_email || '',
      ref: doc.ref_doc || '',
      type: doc.doc_type || 'DEVIS'
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="devis-factures-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">{t('devis.title')}</h2>
            <p className="text-sm text-gray-400">{t('devis.subtitle')}</p>
          </div>
        </div>

        <div className="glass p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="loading-spinner mx-auto"></div>
              <p className="text-gray-400 mt-2">{t('common.loading')}</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-gray-400">{t('devis.noDoc')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-dark-custom text-sm">
                <thead>
                  <tr>
                    <th>{t('common.ref')}</th>
                    <th>{t('devis.type')}</th>
                    <th>{t('common.client')}</th>
                    <th>{t('common.total')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('common.date')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} data-testid={`devis-row-${doc.id}`}>
                      <td className="font-mono text-xs text-cyan-400">
                        {doc.ref_doc}
                        {doc.linked_repair_ref && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30" title={t('devisFactures.linkedTo', { ref: doc.linked_repair_ref })}>
                            <Link2 className="w-2.5 h-2.5" /> {doc.linked_repair_ref}
                          </span>
                        )}
                      </td>
                      <td>{mapLabel(doc.doc_type)}
                        {doc.accept_online_payment && !disableOnlinePayment && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            <CreditCard className="w-2.5 h-2.5" /> SumUp
                          </span>
                        )}
                      </td>
                      <td>
                        <div>
                          <div className="font-semibold">{doc.client_nom || '—'}</div>
                          <div className="text-xs text-gray-400">{doc.client_tel || '—'}</div>
                        </div>
                      </td>
                      <td className="font-bold text-green-400">{formatCurrency(doc.prix_final || 0)}</td>
                      <td>{mapLabel(doc.status)}</td>
                      <td>{doc.created_at ? formatDate(doc.created_at) : '-'}</td>
                      <td>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            onClick={() => handlePrintTicket(doc.id)}
                            className="p-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                            title={t('common.print')}
                            data-testid={`devis-ticket-${doc.id}`}
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePrintA4(doc.id)}
                            className="p-2 rounded-lg bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30"
                            title="A4"
                            data-testid={`devis-a4-${doc.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEmailModal(doc)}
                            className="p-2 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                            title={t('devisFactures.sendByEmail')}
                            data-testid={`devis-email-${doc.id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          {doc.status === 'devis' && (
                            <button
                              onClick={() => handleConvertCommande(doc.id)}
                              className="p-2 rounded-lg bg-orange-500/20 text-orange-300 hover:bg-orange-500/30"
                              title={t('nav.commandes')}
                              data-testid={`devis-convert-${doc.id}`}
                            >
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          )}
                          {isAdmin && (
                          <button
                            onClick={() => handleDelete(doc.id)}
                            className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30"
                            title={t('common.delete')}
                            data-testid={`devis-delete-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                          {/* Attach / Detach to repair */}
                          {(doc.doc_type === 'DEVIS' || doc.doc_type === 'devis') && (
                            doc.linked_repair_ref ? (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(t('devisFactures.detachConfirm', { ref: doc.linked_repair_ref }))) return;
                                  try { await api.post(`/devices/${doc.linked_repair_ref}/detach-devis`); fetchDocuments(); }
                                  catch (err) { alert(tError(err.response?.data?.detail) || t('devisFactures.error')); }
                                }}
                                className="p-2 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"
                                title={t('devisFactures.linkedTo', { ref: doc.linked_repair_ref })}
                                data-testid={`detach-calcul-${doc.id}`}
                              >
                                <Unlink className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setAttachModal({ show: true, devisId: doc.id, devisRef: doc.doc_number || doc.ref_doc, devisTotal: doc.prix_final || doc.total || 0 })}
                                className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                                title={t('devisFactures.attachToRepair')}
                                data-testid={`attach-calcul-${doc.id}`}
                              >
                                <Link2 className="w-4 h-4" />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {emailModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999}}>
          <div className="modal-dark w-full max-w-md">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-400" /> {t('devisFactures.emailModalTitle', { type: mapLabel(emailModal.type) })}</h3>
              <button onClick={() => setEmailModal({ show: false, docId: '', email: '', ref: '', type: '' })} className="text-gray-400 hover:text-white">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-400">{t('devisFactures.refLabel')} <span className="text-cyan-400 font-mono">{emailModal.ref}</span></p>
              <p className="text-xs text-gray-500">{t('devisFactures.a4PdfNote')}</p>
              <div>
                <label className="block text-sm text-gray-300 mb-2">{t('devisFactures.clientEmailLabel')}</label>
                <input
                  type="email"
                  value={emailModal.email}
                  onChange={(e) => setEmailModal({ ...emailModal, email: e.target.value })}
                  placeholder={t('devisFactures.emailPlaceholder')}
                  className="input-dark w-full py-3"
                  data-testid="devis-email-modal-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setEmailModal({ show: false, docId: '', email: '', ref: '', type: '' })}
                  className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailModal.email}
                  className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="devis-email-modal-send"
                >
                  {sendingEmail ? (
                    <div className="loading-spinner w-5 h-5"></div>
                  ) : (
                    <><Send className="w-4 h-4" /> {t('common.send')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AttachDevisModal
        isOpen={attachModal.show}
        onClose={() => setAttachModal({ show: false, devisId: '', devisRef: '', devisTotal: 0 })}
        devisId={attachModal.devisId}
        devisType="calcul"
        devisRef={attachModal.devisRef}
        devisTotal={attachModal.devisTotal}
        onAttached={fetchDocuments}
      />
    </Layout>
  );
};

export default DevisFacturesPage;
